<template>
  <v-container>
    <h1>{{ action }} Subscription</h1>
    <v-form>
      <v-alert
        v-model="isError"
        dismissible
        type="error"
        transition="fade-transition"
      >
        {{ errMessage }}
      </v-alert>
      <v-select
          v-model="scope"
          :items="scopes"
          :disabled="$route.params.id !== 'new'"
          label="Subscription Scope"
          id="scope"
      />
      <component
        :is="scope + '-select'"
        id="scopeID"
        :disabled="$route.params.id !== 'new'"
        v-model="scopeID"
        required
      />

      <v-select
        v-model="billingMethodID"
        :items="billingMethods"
        label="Billing Method"
        item-text="title"
        item-value="id"
        :loading="billingMethodsLoading"
        :disabled="billingMethodIsDisabled"
        id="billingMethodID"
      />

      <v-select
        v-model="pricingDefinitionID"
        :items="pricingDefinitions"
        label="Pricing Definition"
        item-text="title"
        item-value="id"
        :loading="pricingDefinitionsLoading"
        :disabled="pricingDefinitionIsDisabled"
        id="pricingDefinitionID"
      />

      <v-menu
        ref="menu0"
        :close-on-content-click="false"
        v-model="showMenu[0]"
        :nudge-right="40"
        :return-value.sync="dates[0]"
        transition="scale-transition"
        offset-y
        full-width
        min-width="290px"
      >
        <v-text-field
          slot="activator"
          v-model="dates[0]"
          label="Start Date"
          prepend-icon="event"
          readonly
          required
          :error-messages="errors.startDate"
          id="startDate"
          @focus="inputFocus"
        ></v-text-field>
        <v-date-picker v-model="dates[0]" no-title scrollable>
          <v-spacer></v-spacer>
          <v-btn flat color="primary" @click="showMenu[0] = false">Cancel</v-btn>
          <v-btn flat color="primary" @click="$refs.menu0.save(dates[0])">OK</v-btn>
        </v-date-picker>
      </v-menu>

      <v-menu
        ref="menu1"
        :close-on-content-click="false"
        v-model="showMenu[1]"
        :nudge-right="40"
        :return-value.sync="dates[1]"
        transition="scale-transition"
        offset-y
        full-width
        min-width="290px"
      >
        <v-text-field
          slot="activator"
          v-model="dates[1]"
          label="End Date"
          prepend-icon="event"
          readonly
          required
          :error-messages="errors.endDate"
          id="endDate"
          @focus="inputFocus"
        ></v-text-field>
        <v-date-picker v-model="dates[1]" no-title scrollable>
          <v-spacer></v-spacer>
          <v-btn flat color="primary" @click="showMenu[1] = false">Cancel</v-btn>
          <v-btn flat color="primary" @click="$refs.menu1.save(dates[1])">OK</v-btn>
        </v-date-picker>
      </v-menu>

      <v-btn
        @click="submit"
      >Save</v-btn>
    </v-form>
    <template v-if="ppSubscriptionID !== null">
      <hr />
      <h2>PayPal Details</h2>
      <v-btn
        @click="loadPaypalDetails"
        :loading="ppDetailsLoading"
        :disabled="ppDetailsLoading"
      >
        Load Data
      </v-btn>
      <v-btn
        @click="tryPaypalApproval"
        :loading="ppApprovalLoading"
        :disabled="ppApprovalLoading"
      >
        Try Approval
      </v-btn>
      <v-textarea
        :readonly="true"
        v-model="ppDetailsText"
      />
    </template>
  </v-container>
</template>

<script>
import UserSelect from '@/components/user-select'
import ChurchSelect from '@/components/church-select'

export default {
  data: () => ({
    scopes: [
      { text: 'User', value: 'user' },
      { text: 'Church', value: 'church' }
    ],
    scope: 'user',
    scopeID: null,

    ppSubscriptionID: null,
    ppDetailsLoading: false,
    ppApprovalLoading: false,
    ppDetailsText: '',

    billingMethods: [],
    billingMethodsLoading: false,
    billingMethodID: null,

    pricingDefinitions: [],
    pricingDefinitionsLoading: false,
    pricingDefinitionID: null,

    showMenu: [false, false],
    dates: [null, null],

    isError: false,
    errMessage: null,
    errors: {
      startDate: null,
      endDate: null
    }
  }),
  computed: {
    action: function () {
      let id = this.$route.params.id
      if (id === 'new') {
        return 'Add'
      } else {
        return 'Edit'
      }
    },
    billingMethodIsDisabled: function () {
      // The component should always be disabled if we're editing an existing
      // subscription, and also if we're still loading options
      if (this.$route.params.id !== 'new' || this.billingMethodsLoading) {
        return true
      } else {
        return false
      }
    },
    pricingDefinitionIsDisabled: function () {
      // The component should always be disabled if we're editing an existing
      // subscription, and also if we're still loading options
      if (this.$route.params.id !== 'new' || this.pricingDefinitionsLoading) {
        return true
      } else {
        return false
      }
    }
  },
  mounted: function () {
    // Populate billing methods
    this.billingMethodsLoading = true
    this.axios.get('/subscriptions/billing/methods')
      .then((res) => {
        this.billingMethods = res.data
      })
      .catch(this.$root.apiErrorHandler((msg, errors) => {
        this.errMessage = msg
        this.errors = errors
        this.isError = true
      }))
      .finally(() => {
        this.billingMethodsLoading = false
      })

    // Populate pricing definitions
    this.pricingDefinitionsLoading = true
    this.axios.get('/subscriptions/pricing/definitions')
      .then((res) => {
        this.pricingDefinitions = res.data
      })
      .catch(this.$root.apiErrorHandler((msg, errors) => {
        this.errMessage = msg
        this.errors = errors
        this.isError = true
      }))
      .finally(() => {
        this.pricingDefinitionsLoading = false
      })

    let id = this.$route.params.id
    // If we're making a new subscription, we're done
    if (id === 'new') {
      return
    }

    // Otherwise, we need to load data for the subscription
    var _this = this
    this.axios.get('/subscriptions/' + id)
      .then(function (res) {
        let sub = res.data[0]

        _this.scope = sub.scope
        _this.scopeID = sub.scopeID
        _this.ppSubscriptionID = sub.ppSubscriptionID
        _this.billingMethodID = sub.billingMethodID

        // We only want the date part (0000-00-00) of the string, so we get
        // the first 10 characters and exclude any time portion that's present
        _this.dates[0] = sub.startDate.substr(0, 10)
        _this.dates[1] = sub.endDate.substr(0, 10)
      })
      .catch(this.$root.apiErrorHandler(function (msg, errors, err) {
        // If our error is a 404, that means we got a bogus subscription ID value and
        // we can safely abort back to the subscription listing page. Otherwise, we want
        // to render our error message.
        if (err && err.response && err.response.status === 404) {
          return _this.$router.push('/admin/subscriptions')
        } else {
          _this.errMessage = msg
          _this.isError = true
        }
      }))
    this.axios.get('/subscriptions/' + id + '/pricing')
      .then((res) => {
        let pricing = res.data[0]
        this.pricingDefinitionID = pricing.definitionID
      })
      .catch(this.$root.apiErrorHandler((msg) => {
        this.errMessage = msg
        this.isError = true
      }))
  },
  methods: {
    submit: function () {
      var _this = this

      // Format our date values into datetimes to make our API happy
      let startDate = this.dates[0] + ' 00:00:00'
      if (this.dates[0] === null) {
        startDate = ''
      }
      let endDate = this.dates[1] + ' 00:00:00'
      if (this.dates[1] === null) {
        endDate = ''
      }
      let data = {
        startDate,
        endDate
      }

      // Set up the proper request based on whether we're editing or creating
      let url = '/subscriptions/' + this.scope + '/' + this.scopeID
      let method = 'POST'
      let id = this.$route.params.id
      if (id !== 'new') {
        url = '/subscriptions/' + id
        method = 'PUT'
      } else {
        data.billingMethodID = this.billingMethodID
        data.pricingDefinitionID = this.pricingDefinitionID
      }

      this.axios.request({
        method,
        url,
        data
      })
        .then(function () {
          _this.$router.push('/admin/subscriptions')
        })
        .catch(this.$root.apiErrorHandler(function (msg, errors) {
          _this.errMessage = msg
          _this.errors = errors
          _this.isError = true
        }))
    },
    loadPaypalDetails: function () {
      this.ppDetailsLoading = true
      this.axios.get('/subscriptions/billing/paypal/details/' + this.ppSubscriptionID)
        .then((res) => {
          this.ppDetailsText = JSON.stringify(res.data)
        })
        .catch(this.$root.apiErrorHandler())
        .finally(() => {
          this.ppDetailsLoading = false
        })
    },
    tryPaypalApproval: function () {
      this.ppApprovalLoading = true
      this.axios.post('/subscriptions/billing/paypal/approval/' + this.ppSubscriptionID)
        .then(() => {
          this.ppDetailsText = 'Successfully approved'
        })
        .catch(this.$root.apiErrorHandler())
        .finally(() => {
          this.ppApprovalLoading = false
        })
    },
    inputFocus (ev) {
      // Clear any errors for an input when it receives focus
      if (ev.target && ev.target.id) {
        this.errors[ev.target.id] = null
      }
    }
  },
  components: {
    UserSelect,
    ChurchSelect
  }
}
</script>
